import React, { useEffect } from 'react';
import { useError } from '../../contexts/ErrorContext';
import { useLanguage } from '../../contexts/LanguageContext';

const ErrorNotification: React.FC = () => {
  const { error, clearError } = useError();
  const { language, tError, tValidationErrors } = useLanguage();
  const isRTL = language === 'ar';

  const apiError = error?.error ?? undefined;
  const isValidation = apiError?.message_code === 'validation.error';
  const validationItems = isValidation ? tValidationErrors(apiError) : [];
  const localizedMessage = tError(apiError);

  useEffect(() => {
    // Auto-scroll to top when error appears
    if (error) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  if (!error || error.status !== false) {
    return null;
  }

  return (
    <div
      className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4 animate-slide-down"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="bg-red-50 border-l-4 border-red-500 rounded-lg shadow-lg p-4 relative">
        {/* Close button */}
        <button
          onClick={clearError}
          className="absolute top-2 right-2 text-red-400 hover:text-red-600 transition-colors"
          aria-label="Close"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="flex items-start gap-3 pr-6">
          {/* Error icon */}
          <div className="flex-shrink-0">
            <svg
              className="w-6 h-6 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          {/* Error content */}
          <div className="flex flex-col gap-1 flex-1">
            <div className={`text-sm text-red-700 ${isRTL ? 'text-right' : 'text-left'}`}>
              {localizedMessage || (language === 'ar' ? 'حدث خطأ' : 'An error occurred')}
            </div>
            {isValidation && validationItems.length > 0 && (
              <ul
                className={`text-xs text-red-600 mt-1 list-disc ${
                  isRTL ? 'pr-4 text-right' : 'pl-4 text-left'
                }`}
              >
                {validationItems.map((item, idx) => (
                  <li key={`${item.field ?? 'field'}-${idx}`}>{item.message}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorNotification;
